import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, CounterPrinted, CurrentReport, ReportSettings, ZReport } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { readJson, remove, writeJson } from "../../lib/storage";
import { counterToast } from "../printing/print-notices";
import {
  dayName,
  openLines,
  readPlatforms,
  shortDayName,
  xPrint,
  zPrint,
  type KeptPlatforms,
  type PlatformDraft,
  platformFields,
  type ReportPrint,
} from "./reports";
import { StatsBoxes } from "./StatsBoxes";

interface Props {
  /** Who opened Beheer: the code was given before, the Z is one click (Furkan, 11/10/2026). */
  authorization: Authorization;
  settings: ReportSettings | null;
  /** `printed`: the server sent it to the printer of the Z-rapport. */
  onPrint: (print: ReportPrint, printed: boolean) => void;
  onManagePlatforms: () => void;
}

/** What was typed on the Z page, kept on this device until the Z is made. */
interface Draft {
  platforms: PlatformDraft[];
  /** The Z the day had when this was typed (see KeptPlatforms). */
  basedOn?: string | null;
}

/** Waits a moment after a live update, so a burst of sales asks the server once. */
const RELOAD_DELAY_MS = 250;
const draftKey = (day: string) => `tafo.zpage.${day}`;

/**
 * Vandaag, the Z page (Furkan, 11/10/2026): first the online platforms (their day total and what was paid
 * online, 13/10/2026), then the whole day (the X, live; Betaalwijzen also shows the platforms' online and
 * cash on their own) and the day's total, and only then
 * "Z afsluiten en afdrukken". No drawer ("geld in de lade moet er niet bij"). Every day (06:00 to 06:00)
 * has one Z: a new one overwrites the Z the day had (same number, the old one deleted). A day that was forgotten is chosen at the top.
 */
export function DayTab({ authorization, settings, onPrint, onManagePlatforms }: Props) {
  const { api, status, general, report, reportsRevision, toast } = useTill();
  /** The day shown: null is today. */
  const [day, setDay] = useState<string | null>(null);
  const [current, setCurrent] = useState<CurrentReport | null>(null);
  /** What was typed per day on this page; the stored draft of the device until then. */
  const [typed, setTyped] = useState<Record<string, Draft>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  /** The error of the last live reload: shown once, not again with every sale. */
  const failed = useRef<string | null>(null);
  const text = t.reports;
  const page = text.page;

  const load = useCallback(
    () =>
      api.request<CurrentReport>(
        "GET",
        day === null ? "/device/reports/current" : `/device/reports/current?businessDay=${day}`,
        { staffToken: authorization.token, retry: true },
      ),
    [api, authorization.token, day],
  );

  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      load()
        .then((answer) => {
          failed.current = null;
          if (live) setCurrent(answer);
        })
        .catch((failure: unknown) => {
          const code = failure instanceof ApiError ? failure.code : "unexpected";
          if (failed.current === code) return;
          failed.current = code;
          report(failure);
        });
    }, RELOAD_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [load, report, reportsRevision]);

  if (!current) return <div aria-busy="true" />;

  // what was typed for this day comes back, with the shop's platforms of now. After the Z the fields are
  // not emptied (Furkan, 13/10/2026: "waarden niet op 0 zetten wat ik intik tot ik verander"): they show
  // what the day's Z was closed with, on every till, until someone types something else.
  const kept: KeptPlatforms | null =
    typed[current.businessDay] ?? readJson<KeptPlatforms | null>(draftKey(current.businessDay), null);
  const dayZ = current.closed ?? null;
  // gekoppelde platformen (Takeaway/MyMenu): die bestellingen zitten al in de omzet, dus enkel ter info tonen
  // en NIET opnieuw laten intikken. Het handmatige vak blijft voor niet-gekoppelde platformen.
  const autoPlatforms = current.platformsAuto ?? [];
  const autoNames = new Set(autoPlatforms.map((platform) => platform.name.toLowerCase()));
  const manualNames = (settings?.platforms ?? []).filter((name) => !autoNames.has(name.toLowerCase()));
  const draft: Draft = { platforms: platformFields(manualNames, kept, dayZ) };
  const change = (typedNow: Draft) => {
    // typed on top of the Z the day has now: another Z of the day (another till) makes it out of date
    const next: Draft = { ...typedNow, basedOn: dayZ?.closedAt ?? null };
    setTyped({ ...typed, [current.businessDay]: next });
    setError("");
    writeJson(draftKey(current.businessDay), next);
  };
  const setPlatform = (index: number, edit: Partial<PlatformDraft>) => {
    change({
      ...draft,
      platforms: draft.platforms.map((platform, at) => (at === index ? { ...platform, ...edit } : platform)),
    });
  };

  const lines = openLines(current.open);
  const { figures, tooHigh, unreadable } = readPlatforms(draft.platforms);
  const platformRevenue = figures.reduce((total, figure) => total + figure.revenueCents, 0);
  const platformOnline = figures.reduce((total, figure) => total + figure.onlineCents, 0);
  const mayClose = authorization.staff.can.includes("reports.close");
  const problem =
    unreadable.length > 0
      ? text.dialog.badAmount(unreadable.join(", "))
      : tooHigh.length > 0
        ? text.dialog.tooHigh(tooHigh.join(", "))
        : "";
  const blocked = lines.length > 0 || problem !== "" || !mayClose || busy;
  const closed = current.closed ?? null;

  /** "🖨 X-rapport afdrukken": the day as it is now, printed and shown. */
  const printX = () => {
    api
      .request<{ current: CurrentReport; printed: CounterPrinted }>(
        "POST",
        `/device/reports/x/print?businessDay=${current.businessDay}`,
        { staffToken: authorization.token },
      )
      .then(({ current: fresh, printed }) => {
        setCurrent(fresh);
        // "X-rapport → Kassaprinter toog"
        const said = counterToast(printed);
        if (said !== null) toast(said);
        onPrint(xPrint(fresh, status.shop, general, new Date()), printed !== null);
      })
      .catch(report);
  };

  const closeZ = async () => {
    if (blocked) return;
    setBusy(true);
    try {
      const answer = await api.request<{ report: ZReport; printed: CounterPrinted }>("POST", "/device/reports/z", {
        staffToken: authorization.token,
        body: {
          businessDay: current.businessDay,
          // a platform without revenue that day is left out (prototype)
          platforms: figures
            .filter((figure) => figure.revenueCents > 0)
            .map(({ name, revenueCents, onlineCents }) => ({ name, revenueCents, onlineCents })),
        },
      });
      // the fields now show what this Z was closed with (current.closed.platforms)
      remove(draftKey(current.businessDay));
      setTyped({ ...typed, [current.businessDay]: { platforms: [] } });
      // prototype: "Z 12 afgesloten · € 1.234,50 · Z-bon → Kassaprinter toog"
      toast(
        text.dialog.closed(answer.report.number, formatEuro(answer.report.stats.orders.revenueCents)) +
          (answer.printed === null ? "" : t.print.zClosed(answer.printed.printer)),
      );
      onPrint(zPrint(answer.report), answer.printed !== null);
      // after a forgotten day: the next forgotten day, or today
      const rest = current.missing.filter((item) => item.day !== current.businessDay);
      setDay(current.late ? (rest[0]?.day ?? null) : day);
      setCurrent(current.late ? null : await load());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  const title = dayName(current.businessDay);
  return (
    <>
      {current.missing.length > 0 && (
        <div className="warnbox zmissing" role="status">
          <b>{page.missing(current.missing.length)}</b>
          <div className="rowacts tight zdays">
            {current.missing.map((item) => (
              <button
                key={item.day}
                type="button"
                className="btn small"
                aria-pressed={current.businessDay === item.day}
                onClick={() => {
                  setDay(item.day);
                  setCurrent(null);
                }}
              >
                {page.dayButton(shortDayName(item.day), item.orders)}
              </button>
            ))}
            <button
              type="button"
              className="btn small"
              aria-pressed={!current.late}
              onClick={() => {
                setDay(null);
                setCurrent(null);
              }}
            >
              {page.today}
            </button>
          </div>
        </div>
      )}
      <h3 className="zpday">
        {title.charAt(0).toUpperCase() + title.slice(1)}
        {current.late && <span className="pill warn">{page.late}</span>}
      </h3>
      <p className="note rsince">
        {current.late ? page.lateNote : page.liveNote}
        {current.firstOrderAt !== null && current.lastOrderAt !== null && (
          <> {page.ordersFromTo(formatTime(current.firstOrderAt), formatTime(current.lastOrderAt))}</>
        )}
      </p>

      {/* two columns (Furkan, 13/10/2026: "deze hele layout moet overzichtelijker"): on the left what is done
          here, top to bottom (the platforms, the day's total, the Z); on the right the figures of the day */}
      <div className="zpage">
        <div className="zdo">
          {/* 1 · the online platforms first, typed in on the page (Furkan: "meer gefocust op online") */}
          <section className="rbox zplat" aria-label={page.platforms}>
            <h3>{page.platforms}</h3>

            {/* gekoppelde platformen: enkel ter info, zitten al in de omzet (Furkan, 15/10/2026) */}
            {autoPlatforms.length > 0 && (
              <div className="zplat-auto">
                <p className="note zd-note">{text.dialog.linkedNote}</p>
                <div className="tablewrap">
                  <table className="ptab stack">
                    <thead>
                      <tr>
                        <th>{text.dialog.columns.platform}</th>
                        <th className="r">{text.dialog.columns.revenue}</th>
                        <th className="r">{text.dialog.columns.online}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {autoPlatforms.map((platform) => (
                        <tr key={platform.name}>
                          <td className="full">
                            <b>{platform.name}</b>
                          </td>
                          <td className="r" data-label={text.dialog.columns.revenue}>
                            {formatEuro(platform.revenueCents)}
                          </td>
                          <td className="r" data-label={text.dialog.columns.online}>
                            {formatEuro(platform.onlineCents ?? 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p className="note zd-note">{text.dialog.platformsNote}</p>
            <div className="tablewrap">
              <table className="ptab stack">
                <thead>
                  <tr>
                    <th>{text.dialog.columns.platform}</th>
                    <th className="r">{text.dialog.columns.revenue}</th>
                    <th className="r">{text.dialog.columns.online}</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.platforms.map((platform, index) => {
                    return (
                      <tr key={platform.name}>
                        <td className="full">
                          <b>{platform.name}</b>
                        </td>
                        <td className="r" data-label={text.dialog.columns.revenue}>
                          <input
                            className="num"
                            inputMode="decimal"
                            placeholder="0"
                            aria-label={text.dialog.revenueOf(platform.name)}
                            value={platform.revenue}
                            onChange={(event) => {
                              setPlatform(index, { revenue: event.target.value });
                            }}
                          />
                        </td>
                        <td className="r" data-label={text.dialog.columns.online}>
                          <input
                            className="num"
                            inputMode="decimal"
                            placeholder="0"
                            aria-label={text.dialog.onlineOf(platform.name)}
                            value={platform.online}
                            onChange={(event) => {
                              setPlatform(index, { online: event.target.value });
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {draft.platforms.length === 0 && (
                    <tr>
                      <td colSpan={3} className="note">
                        {autoPlatforms.length > 0 ? text.dialog.noneMore : text.dialog.none}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <button type="button" className="linkbtn zd-manage" onClick={onManagePlatforms}>
              {text.dialog.manage}
            </button>
          </section>

          {/* 2 · the day's total; no drawer */}
          <section className="rbox ztotal" aria-label={page.total}>
            <h3>{page.total}</h3>
            <div className="kvs">
              <div>
                <span>{page.till}</span>
                <b className="num">{formatEuro(current.stats.orders.revenueCents)}</b>
              </div>
              {/* only once a platform has an amount (Furkan, 13/10/2026: "geen overbodige info") */}
              {platformRevenue !== 0 && (
                <div>
                  <span>{page.platformsRevenue}</span>
                  <b className="num">{formatEuro(platformRevenue)}</b>
                </div>
              )}
              <div className="zsum">
                <span>{page.dayTotal}</span>
                <b className="num">{formatEuro(current.stats.orders.revenueCents + platformRevenue)}</b>
              </div>
            </div>
            {/* the linked platforms, as on the Z-bon (Antalya, 17/10/2026): how much of the till's revenue came
                through each; already in it, so never added */}
            {autoPlatforms.length > 0 && (
              <div className="kvs zlinked" role="group" aria-label={page.linked}>
                <b>{page.linked}</b>
                <p className="note">{page.linkedNote}</p>
                {autoPlatforms.map((platform) => (
                  <div key={platform.name}>
                    <span>
                      {platform.name}
                      {platform.onlineCents !== undefined && (
                        <small> · {page.linkedOnline(formatEuro(platform.onlineCents))}</small>
                      )}
                    </span>
                    <b className="num">{formatEuro(platform.revenueCents)}</b>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* 3 · only then the Z */}
          <section className="rbox zbox2" aria-label={text.closing}>
            <h3>{text.closing}</h3>
            {lines.length > 0 && (
              <div className="warnbox" role="status">
                <b>{text.finishFirst}</b>
                {lines.map((line) => (
                  <span key={line}>
                    <br />
                    {line}
                  </span>
                ))}
              </div>
            )}
            {closed && <p className="note">{page.closed(closed.number, formatTime(closed.closedAt))}</p>}
            {closed && (closed.after ?? 0) > 0 && (
              <p className="banner warn">{page.after(closed.after ?? 0, closed.number)}</p>
            )}
            {!mayClose && <p className="note">{page.noRight(authorization.staff.name)}</p>}
            <p className="pinmsg left" role="alert">
              {problem || error}
            </p>
            <div className="rowacts zacts">
              <button type="button" className="btn" onClick={printX}>
                {text.printX}
              </button>
              <button type="button" className="btn primary" disabled={blocked} onClick={() => void closeZ()}>
                {closed
                  ? page.closeAgain(closed.number)
                  : current.late
                    ? page.closeLate(shortDayName(current.businessDay))
                    : text.closeZ}
              </button>
            </div>
          </section>
        </div>

        {/* the whole day, live with what is typed for the platforms */}
        <StatsBoxes
          stats={current.stats}
          platforms={{
            revenueCents: platformRevenue,
            onlineCents: platformOnline,
            cashCents: Math.max(0, platformRevenue - platformOnline),
          }}
        />
      </div>
    </>
  );
}
